import { useMemo, useState } from "react";

import dayjs from "dayjs";
import { Download, MoreHorizontal, Search, TrendingUp } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { StatCardsSkeleton, TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { useOverconsumptions, useReverseOverconsumption } from "~/hooks/production";
import { useToast } from "~/hooks/use-toast";
import { usePagination } from "~/hooks/usePagination";
import {
  PeriodSelector,
  useCosteoPeriod,
} from "~/pages/costing/components/CosteoShared";
import { useProfileStore } from "~/store/profileStore";
import { UNIT_LABELS } from "~/types/purchasing";
import { exportCsv } from "~/utils/csv";
import { formatCurrency } from "~/utils/helpers";

import type { OverconsumptionRecord } from "~/types/production";

const num = (n: number) =>
  new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(n);

// Unlike mermas, sobreconsumo oil quantities are already stored in kg — no conversion.
const displayUnit = (r: OverconsumptionRecord) =>
  r.item_kind === "oil"
    ? "kg"
    : (UNIT_LABELS[r.item_unit ?? ""] ?? r.item_unit ?? "");

export function SobreconsumosTab() {
  const { toast } = useToast();
  const role = useProfileStore((s) => s.role);
  const canWrite =
    role === "superadmin" || role === "admin" || role === "produccion-admin";

  const { periods, period, setPeriod } = useCosteoPeriod();
  const [plant, setPlant] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [revertTarget, setRevertTarget] =
    useState<OverconsumptionRecord | null>(null);

  const { data = [], isLoading, refetch } = useOverconsumptions({
    period: period || undefined,
    plant: plant === "all" ? undefined : plant,
  });
  const reverse = useReverseOverconsumption();

  const openPeriods = useMemo(
    () => new Set(periods.filter((p) => p.status === "open").map((p) => p.period)),
    [periods]
  );
  // Revert eligibility follows the lote's costing period (batch end date), not the
  // registration date — reverse_batch_overconsumption asserts open on it.
  const isRowOpen = (r: OverconsumptionRecord) =>
    openPeriods.has(r.batch_period);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data;
    return data.filter(
      (r) =>
        (r.item_name ?? "").toLowerCase().includes(term) ||
        (r.item_code ?? "").toLowerCase().includes(term) ||
        (r.batch_number ?? "").toLowerCase().includes(term) ||
        (r.product_name ?? "").toLowerCase().includes(term) ||
        (r.product_code ?? "").toLowerCase().includes(term) ||
        (r.reason ?? "").toLowerCase().includes(term)
    );
  }, [data, search]);

  const kpis = useMemo(() => {
    let oils = 0;
    let mps = 0;
    let cost = 0;
    for (const r of rows) {
      if (r.item_kind === "oil") oils++;
      else mps++;
      cost += r.total_cost_clp || 0;
    }
    return { total: rows.length, oils, mps, cost };
  }, [rows]);

  const { page, setPage, pageCount, pageItems, total } = usePagination(rows, 20);

  const handleExport = () => {
    exportCsv(
      `sobreconsumos_${period || "todos"}.csv`,
      [
        "Fecha",
        "Tipo",
        "Código",
        "Ítem",
        "Planta",
        "Lote",
        "Producto",
        "Cantidad",
        "Unidad",
        "Costo CLP",
        "Motivo",
      ],
      rows.map((r) => [
        dayjs(r.movement_date).format("DD/MM/YYYY"),
        r.item_kind === "oil" ? "Aceite" : "Materia Prima",
        r.item_code ?? "",
        r.item_name ?? "",
        r.plant ?? "",
        r.batch_number ?? "",
        r.product_name ?? "",
        num(r.quantity),
        displayUnit(r),
        Math.round(r.total_cost_clp || 0),
        r.reason ?? "",
      ])
    );
  };

  const handleRevert = () => {
    if (!revertTarget) return;
    reverse.mutate(
      { pbiId: revertTarget.id, batchId: revertTarget.batch_id },
      {
        onSuccess: () => {
          toast({
            title: "Sobreconsumo revertido",
            description:
              "Se restauró el stock del ingrediente y el costo del lote.",
          });
          setRevertTarget(null);
          refetch();
        },
        onError: (error) => {
          setRevertTarget(null);
          toast({
            title: "No se pudo revertir",
            description: (error as Error).message,
            variant: "destructive",
          });
        },
      }
    );
  };

  if (isLoading) {
    return (
      <>
        <StatCardsSkeleton count={4} />
        <TableSkeleton rows={10} columns={9} />
      </>
    );
  }

  return (
    <>
      <p className="text-sm text-muted-foreground">
        Consumo adicional sobre la receta, registrado en lotes completados; su
        costo se suma al costo del producto terminado.
      </p>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total sobreconsumos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{kpis.total}</p>
            <p className="text-xs text-muted-foreground">
              {kpis.mps} MP · {kpis.oils} aceites
            </p>
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Costo total sobreconsumo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-red-700">
              ${formatCurrency(kpis.cost)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Período
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{period || "—"}</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <PeriodSelector periods={periods} value={period} onChange={setPeriod} />
        <div className="relative sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar ítem, lote, producto o motivo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-9"
          />
        </div>
        <Select value={plant} onValueChange={setPlant}>
          <SelectTrigger className="h-9 w-full sm:w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las plantas</SelectItem>
            <SelectItem value="P1">Planta 1</SelectItem>
            <SelectItem value="P2">Planta 2</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={handleExport}
          disabled={rows.length === 0}
        >
          <Download className="mr-2 h-4 w-4" />
          Exportar CSV
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Ingrediente</TableHead>
                  <TableHead>Lote</TableHead>
                  <TableHead>Producto</TableHead>
                  <TableHead>Planta</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  <TableHead className="text-right">Costo</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="h-40 p-0">
                      <EmptyState
                        icon={TrendingUp}
                        title="Sin sobreconsumos"
                        description="No hay sobreconsumos para estos filtros. Se registran desde el detalle del lote completado."
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  pageItems.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {dayjs(r.movement_date).format("DD/MM/YYYY")}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {r.item_kind === "oil" ? "Aceite" : "Materia Prima"}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[240px]">
                        <div className="truncate font-medium">
                          {r.item_name ?? "—"}
                        </div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {r.item_code}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {r.batch_number ?? "—"}
                      </TableCell>
                      <TableCell className="max-w-[240px]">
                        <div className="truncate">{r.product_name ?? "—"}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {r.product_code}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {r.plant ?? "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {num(r.quantity)}{" "}
                        <span className="text-xs text-muted-foreground">
                          {displayUnit(r)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums text-red-700">
                        ${formatCurrency(r.total_cost_clp || 0)}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-muted-foreground">
                        {r.reason ?? "—"}
                      </TableCell>
                      <TableCell>
                        {canWrite && isRowOpen(r) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-700"
                                onClick={() => setRevertTarget(r)}
                              >
                                Revertir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {rows.length > 0 && (
            <div className="border-t p-4">
              <TablePagination
                page={page}
                pageCount={pageCount}
                totalItems={total}
                pageSize={20}
                onPageChange={setPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={!!revertTarget}
        onOpenChange={(o) => !o && setRevertTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revertir sobreconsumo</AlertDialogTitle>
            <AlertDialogDescription>
              Se restaurará el stock de {revertTarget?.item_name} en{" "}
              {revertTarget?.plant} y se descontará el costo del lote{" "}
              {revertTarget?.batch_number}. ¿Confirmar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reverse.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => {
                e.preventDefault();
                handleRevert();
              }}
              disabled={reverse.isPending}
            >
              Revertir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
